// Inviter quelqu'un dans le cabinet (avocats uniquement).
//
// C'est le seul moyen d'ouvrir un compte : sans invitation, personne ne peut
// poser de mot de passe. Le jeton n'est affiché **qu'une fois** — le serveur
// n'en garde que l'empreinte — et doit être transmis hors bande.

import { useState } from 'react';
import { api } from '../api/client';
import { useAuth } from './store';

const USER_TYPES = [
  { value: 'client_perso', label: 'Client (particulier)' },
  { value: 'client_entreprise_mandataire', label: 'Client (entreprise — mandataire)' },
  { value: 'client_entreprise_user', label: 'Client (entreprise — utilisateur)' },
  { value: 'avocat_collaborateur', label: 'Avocat collaborateur' },
  { value: 'avocat_titulaire', label: 'Avocat titulaire' },
  { value: 'tiers', label: 'Tiers (huissier, expert…)' },
];

type Issued = { invitationToken: string; expiresAt: string };

export function InviteToCabinet() {
  const session = useAuth((s) => s.session);

  const [email, setEmail] = useState('');
  const [userType, setUserType] = useState('client_perso');
  const [issued, setIssued] = useState<Issued | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!session) return null;

  /** Lien prêt à transmettre : le formulaire d'activation le lit tout seul. */
  const inviteLink = issued
    ? `${window.location.origin}/?invitation=${encodeURIComponent(issued.invitationToken)}`
    : '';

  async function onInvite(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setIssued(null);
    try {
      setIssued(
        await api<Issued>('/invitations', {
          method: 'POST',
          token: session!.sessionToken,
          body: { email: email.trim(), userType },
        }),
      );
      setEmail('');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onInvite} className="mt-6 border-t border-slate-200 pt-6 text-left">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Inviter au cabinet
      </h3>
      <p className="mt-1 text-xs text-slate-400">
        Ouvre un compte et remet une invitation à usage unique. Sans elle, nul ne
        peut définir de mot de passe.
      </p>

      <div className="mt-3 flex gap-2">
        <input
          className="flex-1 rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none"
          type="email"
          placeholder="adresse e-mail"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="cabinet-invite-email"
        />
        <select
          className="rounded border border-slate-300 px-2 py-2 text-sm"
          value={userType}
          onChange={(e) => setUserType(e.target.value)}
          data-testid="cabinet-invite-type"
        >
          {USER_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <button
          type="submit"
          disabled={busy || !email.trim()}
          data-testid="cabinet-invite-submit"
          className="rounded bg-blue-900 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50"
        >
          {busy ? '…' : 'Inviter'}
        </button>
      </div>

      {error && (
        <p data-testid="cabinet-invite-error" className="mt-3 text-sm text-red-600">
          {error}
        </p>
      )}

      {issued && (
        <div className="mt-4 rounded border border-emerald-200 bg-emerald-50 p-3">
          <p className="text-xs font-medium text-emerald-800">
            Invitation créée — elle ne sera plus jamais réaffichée.
          </p>
          <textarea
            readOnly
            data-testid="cabinet-invite-link"
            className="mt-2 h-20 w-full rounded border border-emerald-200 bg-white px-2 py-1 font-mono text-[10px] text-slate-700"
            value={inviteLink}
          />
          <button
            type="button"
            onClick={() => void navigator.clipboard?.writeText(inviteLink)}
            className="mt-1 text-xs text-emerald-800 underline"
          >
            Copier le lien
          </button>
          <p className="mt-2 text-[10px] text-emerald-700">
            Valable jusqu&apos;au{' '}
            {new Date(issued.expiresAt).toLocaleString('fr-FR')}. À transmettre par
            un canal distinct.
          </p>
        </div>
      )}
    </form>
  );
}
